import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Send } from 'lucide-react'
import { toast } from 'sonner'
import {
  SettingsCard,
  SettingsRow,
  SettingsSection,
  settingsInputClass,
} from '@/components/app/settings'
import { Button } from '@/components/ui/button'
import { sendTestNotification } from '@/lib/api/features/notifications/api/sendTestNotification'
import useLoginStore from '@/store/LoginStore'
import { logger } from '@/lib/monitoring/logger'
import { LogCategory } from '@/lib/monitoring/types'

/**
 * Developer-only push sender. Moved off the Notifications page into the
 * Developer group (design 15) with the same gate as before: never rendered
 * in production builds.
 */
export function PushTestSection() {
  const workspaceId = useLoginStore((s) => s.workspaceId)
  if (import.meta.env.PROD) return null
  return <NotificationTestCard workspaceId={workspaceId} />
}

function NotificationTestCard({ workspaceId }: { workspaceId: string | null }) {
  const { t } = useTranslation('settings')
  const [title, setTitle] = useState('')
  const [message, setMessage] = useState('')
  const [isSending, setIsSending] = useState(false)

  const handleSend = async () => {
    if (!workspaceId) {
      toast.error(t('pushTesting.toast.noWorkspace'))
      return
    }
    setIsSending(true)
    try {
      const result = await sendTestNotification(
        workspaceId,
        title || undefined,
        message || undefined,
      )

      const nothingEligible =
        result.eligibleWebSubscriptions === 0 && result.eligibleMobileDevices === 0
      const webMisconfigured =
        result.eligibleWebSubscriptions > 0 && !result.webPushConfigured

      if (nothingEligible) {
        toast.warning(result.message)
      } else if (webMisconfigured) {
        toast.warning(t('pushTesting.toast.webMisconfigured', { message: result.message }))
      } else {
        toast.success(result.message)
      }
    } catch (error: unknown) {
      const e = error as { response?: { data?: { error?: string } }; message?: string } | null
      const errorMessage =
        e?.response?.data?.error || e?.message || t('pushTesting.toast.failed')
      toast.error(errorMessage)
      logger.logError(error as Error, LogCategory.NOTIFICATION, {
        component: 'NotificationTestCard',
        action: 'send_test_notification',
      })
    } finally {
      setIsSending(false)
    }
  }

  return (
    <SettingsSection title={t('pushTesting.sectionTitle')} description={t('pushTesting.sectionDescription')}>
      <SettingsCard>
        <SettingsRow label={t('pushTesting.fields.title')} htmlFor="test-notification-title" control="field">
          <input
            id="test-notification-title"
            className={settingsInputClass}
            placeholder={t('pushTesting.fields.titlePlaceholder')}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={isSending}
          />
        </SettingsRow>
        <SettingsRow label={t('pushTesting.fields.message')} htmlFor="test-notification-message" control="field">
          <input
            id="test-notification-message"
            className={settingsInputClass}
            placeholder={t('pushTesting.fields.messagePlaceholder')}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            disabled={isSending}
          />
        </SettingsRow>
        <SettingsRow label={t('pushTesting.sendRowLabel')} description={t('pushTesting.sendHint')}>
          <Button onClick={handleSend} disabled={isSending} size="sm">
            <Send className="size-4" />
            {isSending ? t('pushTesting.sending') : t('pushTesting.send')}
          </Button>
        </SettingsRow>
      </SettingsCard>
    </SettingsSection>
  )
}
